'use client';

import { useRef } from 'react';
import Image from 'next/image';
import { PencilIcon } from '@heroicons/react/24/outline';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { Spinner } from '@/components/platform/ui';

interface DropUploadProps {
  imageUrl: string | null;
  onFileSelect: (file: File) => void;
  onEdit?: () => void;
  uploading?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  shape?: 'circle' | 'square';
  showOverlay?: boolean;
  disabled?: boolean;
  accept?: string;
  useImgTag?: boolean; // Use native img tag instead of Next.js Image (for external sponsor logos)
}

const sizeClasses = {
  sm: 'size-12',
  md: 'size-16',
  lg: 'size-24',
  xl: 'size-32',
};

export default function DropUpload({
  imageUrl,
  onFileSelect,
  onEdit,
  uploading = false,
  size = 'md',
  shape = 'circle',
  showOverlay = true,
  disabled = false,
  accept = 'image/*',
  useImgTag = false,
}: DropUploadProps) {
  const { darkMode } = useDarkMode();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const roundedClass = shape === 'circle' ? 'rounded-full' : 'rounded-lg';

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && !disabled && !uploading) {
      onFileSelect(file);
      // Clear the input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClick = () => {
    if (!disabled && !uploading) {
      if (imageUrl && onEdit) {
        // If image exists and onEdit callback is provided, call it (to reopen crop modal)
        onEdit();
      } else {
        // Otherwise, open file input to select new image
        fileInputRef.current?.click();
      }
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || uploading) return;

    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      onFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const sizeClass = sizeClasses[size];

  return (
    <div className="shrink-0 p-0.5">
      <div
        className={`relative ${sizeClass} ${roundedClass} group cursor-pointer ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {!imageUrl && !uploading && (
          <svg className={`absolute inset-0 ${sizeClass}`} viewBox="0 0 64 64" fill="none">
            {shape === 'circle' ? (
              <circle
                cx="32"
                cy="32"
                r="30"
                stroke="currentColor"
                strokeWidth="2"
                strokeDasharray="4 4"
                className={`transition-colors ${darkMode ? 'text-white/20 group-hover:text-white/40' : 'text-gray-300 group-hover:text-gray-400'}`}
              />
            ) : (
              <rect
                x="1"
                y="1"
                width="62"
                height="62"
                rx="4"
                stroke="currentColor"
                strokeWidth="1"
                strokeDasharray="4 4"
                fill="none"
                className={`transition-colors ${darkMode ? 'text-white/20 group-hover:text-white/40' : 'text-gray-300 group-hover:text-gray-400'}`}
              />
            )}
          </svg>
        )}
        {imageUrl ? (
          <>
            {useImgTag ? (
              /* Use native img tag for sponsor logos (supports arbitrary external domains) */
              // eslint-disable-next-line @next/next/no-img-element -- sponsor logos come from arbitrary external domains (cannot enumerate in remotePatterns)
              <img
                src={imageUrl}
                alt="Uploaded image"
                className={`${sizeClass} ${roundedClass} object-cover`}
              />
            ) : (
              /* Use Next.js Image for avatars and internal images */
              <Image
                src={imageUrl}
                alt="Uploaded image"
                width={64}
                height={64}
                className={`${sizeClass} ${roundedClass} object-cover`}
                unoptimized={
                  !imageUrl.startsWith('data:') &&
                  !imageUrl.includes('supabase.co') &&
                  !imageUrl.startsWith('/')
                }
              />
            )}
            {showOverlay && !uploading && (
              <div
                className={`absolute inset-0 ${roundedClass} flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100 ${
                  darkMode ? 'bg-gray-900/80' : 'bg-black/50'
                }`}
              >
                <PencilIcon className="size-6 text-white" />
              </div>
            )}
          </>
        ) : uploading ? (
          <div
            className={`${sizeClass} ${roundedClass} flex items-center justify-center ${
              darkMode ? 'bg-white/5' : 'bg-gray-50'
            }`}
          >
            <Spinner size="sm" darkMode={darkMode} />
          </div>
        ) : (
          <div
            className={`${sizeClass} ${roundedClass} flex flex-col items-center justify-center transition-colors ${
              darkMode ? 'bg-white/5 group-hover:bg-white/10' : 'bg-gray-50 group-hover:bg-gray-100'
            }`}
          >
            <svg
              className={`size-6 ${darkMode ? 'text-gray-400 group-hover:text-gray-300' : 'text-gray-400 group-hover:text-gray-500'}`}
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
              />
            </svg>
          </div>
        )}
        {imageUrl && (
          <span
            aria-hidden="true"
            className={`absolute inset-0 ${roundedClass} shadow-inner ${darkMode ? 'ring-1 ring-white/10' : ''}`}
          />
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
}
